import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
    fetchServiceDetails,
    updateService,
    fetchServiceTools,
    updateServiceTool,
    uploadServiceLogo,
    deleteServiceLogo,
} from '../../../api/services';
import { Edit, Upload, Trash2 } from 'lucide-react';
import JsonEditor from '../../common/JsonEditor';
import ServiceLogo from '../../common/logo/ServiceLogo';
import Button from '../../common/button/Button';

import styles from './styles/ServiceDetails.module.css';

const SUCCESS_MESSAGE_TIMEOUT = 3000;

const ROWS_JSON_EDITOR = 30;
const ROWS_DESCRIPTION = 3;

const calculateRows = content => {
    if (!content) return 3;
    const lines = typeof content === 'string'
        ? content.split('\n').length
        : JSON.stringify(content, null, 2).split('\n').length;
    return Math.min(Math.max(lines, 3), ROWS_JSON_EDITOR);
};

// TODO: Add link to the service site
// TODO: Add creating tools
// TODO: Add activating and deactivating service and tools (verified -> isVerified + isEnabled)

const ServiceSettingsView = ({ service, onEdit }) => {
    return (
        <div className={styles.secretItem}>
            <div>
                <div className={styles.oauthDetail}>
                    <strong>Logo:</strong> {service.logo ? 'Yes' : 'No'}
                </div>
                <div className={styles.oauthDetail}>
                    <strong>Name:</strong> {service.name}
                </div>
                <div className={styles.oauthDetail}>
                    <strong>Description:</strong> {service.description || 'No description'}
                </div>
                <div className={styles.oauthDetail}>
                    <strong>OAuth Authentication Required:</strong> {service.requiresAuth ? 'Yes' : 'No'}
                </div>
            </div>
            <div className={styles.secretActions}>
                <Button variant="secondary" size="small" onClick={() => onEdit(service)}>
                    <Edit size={14} />
                </Button>
            </div>
        </div>
    );
};

const ServiceSettingsEditor = ({ serviceData, loading, onCancel, onSubmit, onChange, onLogoUpload, onLogoDelete, error }) => {
    return (
        <div className={styles.blockEditor}>
            <div className={styles.inputGroup}>
                <label className={styles.label}>Logo:</label>
                <div className={styles.logoPreview}>
                    <ServiceLogo
                        logo={serviceData.logo}
                        name={serviceData.name}
                        size="large"
                    />
                    <div className={styles.logoActions}>
                        <Button variant="secondary" size="small" onClick={onLogoUpload}>
                            {serviceData.logo ?
                                <><Edit size={14} /> Change Logo</>
                            :
                                <><Upload size={14} /> Upload Logo</>
                            }
                        </Button>
                        {serviceData.logo && (
                            <Button variant="danger" size="small" onClick={onLogoDelete}>
                                <Trash2 size={14} />
                                Delete Logo
                            </Button>
                        )}
                    </div>
                </div>
                <span className={styles.secretNote}>
                    Logo must be an image file (PNG or JPG), must have 1:1 aspect ratio (square), and less than 5MB in size.
                </span>
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Slug:</label>
                <input
                    type='text'
                    value={serviceData.slug}
                    disabled
                />
                <span className={styles.secretNote}>Slug cannot be changed</span>
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Name:</label>
                <input
                    type='text'
                    name='name'
                    value={serviceData.name}
                    onChange={onChange}
                />
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Description:</label>
                <textarea
                    name='description'
                    value={serviceData.description || ''}
                    onChange={onChange}
                    rows={ROWS_DESCRIPTION}
                />
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>
                    <input
                        type='checkbox'
                        name='requiresAuth'
                        checked={serviceData.requiresAuth}
                        onChange={onChange}
                        className={styles.checkbox}
                    />
                    Requires OAuth Authentication
                </label>
                <span className={styles.secretNote}>
                    If this is enabled, the service will require OAuth authentication. OAuth settings can be found in the OAuth tab.
                </span>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.editorActions}>
                <Button
                    type='submit'
                    variant="primary"
                    size="small"
                    disabled={loading}
                    onClick={onSubmit}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="secondary" size="small" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};

const ToolListItem = ({ tool, onEdit }) => {
    return (
        <div className={styles.secretItem}>
            <div>
                <div className={styles.oauthDetail}>
                    <strong>{tool.name}</strong>
                </div>
                <div className={styles.oauthDetail}>
                    {tool.description || 'No description'}
                </div>
            </div>
            <div className={styles.secretActions}>
                <Button variant="secondary" size="small" onClick={() => onEdit(tool)}>
                    <Edit size={14} />
                </Button>
            </div>
        </div>
    );
};

const ToolEditor = ({ tool, loading, onCancel, onSubmit, onChange, onJsonChange, error }) => {
    const [jsonErrors, setJsonErrors] = useState({
        requestConfig: [],
        parametersSchema: [],
    });
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Check if there are any validation errors in any JSON editor
    const hasJsonErrors = useMemo(() => {
        return Object.values(jsonErrors).some(errors => errors.length > 0);
    }, [jsonErrors]);

    // Handle JSON validation errors
    const handleJsonValidation = useCallback((field, errors) => {
        setJsonErrors(prev => ({
            ...prev,
            [field]: errors
        }));
    }, []);

    // Handle save button click
    const handleSaveClick = useCallback(() => {
        setShowValidationErrors(true);

        // Only submit if there are no errors
        if (!hasJsonErrors) {
            onSubmit(tool.id);
        }
    }, [hasJsonErrors, onSubmit, tool]);

    return (
        <div className={styles.blockEditor}>
            <h3 className={styles.toolName}>{tool.name}</h3>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Slug:</label>
                <input
                    type='text'
                    value={tool.slug}
                    disabled
                />
                <span className={styles.secretNote}>Slug cannot be changed</span>
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Name:</label>
                <input
                    type='text'
                    value={tool.name}
                    onChange={e => onChange(tool.id, 'name', e.target.value)}
                />
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Description:</label>
                <textarea
                    value={tool.description || ''}
                    onChange={e => onChange(tool.id, 'description', e.target.value)}
                    rows={ROWS_DESCRIPTION}
                />
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Request Config (JSON):</label>
                <JsonEditor
                    value={tool.requestConfig}
                    onChange={value => onJsonChange(tool.id, 'requestConfig', value)}
                    rows={calculateRows(tool.requestConfig)}
                    onValidate={errors => handleJsonValidation('requestConfig', errors)}
                    showErrors={showValidationErrors}
                />
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label}>Parameters Schema (JSON):</label>
                <JsonEditor
                    value={tool.parametersSchema}
                    onChange={value => onJsonChange(tool.id, 'parametersSchema', value)}
                    rows={calculateRows(tool.parametersSchema)}
                    onValidate={errors => handleJsonValidation('parametersSchema', errors)}
                    showErrors={showValidationErrors}
                />
            </div>

            {showValidationErrors && hasJsonErrors && (
                <div className={styles.error}>
                    Please fix JSON validation errors before saving.
                </div>
            )}

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.editorActions}>
                <Button
                    onClick={handleSaveClick}
                    variant="primary"
                    size="small"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="secondary" size="small" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};

const ServiceSettings = ({ service, setServices }) => {
    const [serviceData, setServiceData] = useState(null);
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);

    // Split error into separate states for service and tools
    const [serviceError, setServiceError] = useState(null);
    const [toolError, setToolError] = useState(null);
    const [logoError, setLogoError] = useState(null);

    // Split success message into separate states for service and tools
    const [serviceSuccess, setServiceSuccess] = useState('');
    const [toolSuccess, setToolSuccess] = useState('');
    const [logoSuccess, setLogoSuccess] = useState('');

    // State to track what's being edited
    const [editingService, setEditingService] = useState(null);
    const [editingTool, setEditingTool] = useState(null);

    // Keep a copy of the original data for cancel operations
    const [originalTools, setOriginalTools] = useState([]);

    // Reference to file input element
    const fileInputRef = useRef(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch service details
            const serviceDetails = await fetchServiceDetails(service.id);
            setServiceData(serviceDetails);

            // Fetch service tools
            const toolsData = await fetchServiceTools(service.id);
            setTools(toolsData);
            // Store the original data for reset purposes
            setOriginalTools(JSON.parse(JSON.stringify(toolsData)));
        } catch (err) {
            console.error('Error loading service settings:', err);
            setServiceError('Failed to load service settings. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleServiceChange = useCallback(e => {
        const { name, value, type, checked } = e.target;
        setEditingService(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    const handleServiceSubmit = useCallback(async () => {
        try {
            setLoading(true);
            await updateService(service.id, {
                name: editingService.name,
                description: editingService.description,
                requiresAuth: editingService.requiresAuth,
            });

            setServiceSuccess('Service settings updated successfully');
            setTimeout(() => setServiceSuccess(''), SUCCESS_MESSAGE_TIMEOUT);
            setServiceData(editingService);
            setEditingService(null);
            setServiceError(null); // Clear any service errors

            // Update the services list in the parent component
            setServices(prevServices =>
                prevServices.map(s =>
                    s.id === service.id ? { ...s, ...editingService } : s
                )
            );
        } catch (err) {
            console.error('Error updating service:', err);
            setServiceError(err.message || 'Failed to update service settings. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [editingService, service, setServices]);

    // Handle canceling service edit - reset to original values
    const handleServiceCancel = useCallback(() => {
        setEditingService(null);
        setServiceError(null); // Clear service errors on cancel
    }, []);

    const handleToolChange = useCallback((toolId, field, value) => {
        setTools(prev =>
            prev.map(tool =>
                tool.id === toolId ? { ...tool, [field]: value } : tool
            )
        );

        setEditingTool(prev => 
            prev && prev.id === toolId ? { ...prev, [field]: value } : prev
        );
    }, []);

    const handleJsonChange = useCallback((toolId, field, value) => {
        handleToolChange(toolId, field, value);
    }, [handleToolChange]);

    const handleToolSubmit = useCallback(async toolId => {
        try {
            setLoading(true);
            const tool = tools.find(t => t.id === toolId);
            await updateServiceTool(service.id, toolId, {
                name: tool.name,
                description: tool.description,
                requestConfig: tool.requestConfig,
                parametersSchema: tool.parametersSchema,
            });

            setToolSuccess(`Tool "${tool.name}" updated successfully`);
            setTimeout(() => setToolSuccess(''), SUCCESS_MESSAGE_TIMEOUT);
            setEditingTool(null);
            setToolError(null); // Clear any tool errors

            // Update originalTools with the new data after successful save
            setOriginalTools(JSON.parse(JSON.stringify(tools)));
        } catch (err) {
            console.error('Error updating tool:', err);
            // Display the specific error message from the server
            setToolError(err.message || 'Failed to update tool settings. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [tools, service]);

    // Handle canceling tool edit - reset to original values
    const handleToolCancel = useCallback(() => {
        // Restore the original tool data
        setTools(originalTools);
        setEditingTool(null);
        setToolError(null); // Clear tool errors on cancel
    }, [originalTools]);

    // Handle logo upload
    const handleLogoUpload = useCallback(() => {
        // Trigger hidden file input click
        fileInputRef.current.click();
    }, []);

    // Handle file change
    const handleFileChange = useCallback(async e => {
        const file = e.target.files[0];
        if (!file) return;

        // Client-side file type validation
        const allowedTypes = ['image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            setLogoError('Only PNG and JPG files are allowed');
            setTimeout(() => setLogoError(''), SUCCESS_MESSAGE_TIMEOUT);
            e.target.value = '';
            return;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setLogoError('Image file is too large (max 5MB)');
            setTimeout(() => setLogoError(''), SUCCESS_MESSAGE_TIMEOUT);
            e.target.value = '';
            return;
        }

        try {
            setLoading(true);
            setLogoError(null);

            const result = await uploadServiceLogo(service.id, file);

            // Update service data with new logo
            if (editingService) {
                setEditingService(prev => ({ ...prev, logo: result.logo }));
            } else {
                setServiceData(prev => ({ ...prev, logo: result.logo }));
            }

            // Update the services list in the parent component
            setServices(prevServices =>
                prevServices.map(s =>
                    s.id === service.id ? { ...s, logo: result.logo } : s
                )
            );

            setLogoSuccess('Logo uploaded successfully');
            setTimeout(() => setLogoSuccess(''), SUCCESS_MESSAGE_TIMEOUT);
        } catch (err) {
            console.error('Error uploading logo:', err);

            // Try to extract error message from response
            let errorMessage;
            try {
                // If the error has a response with JSON
                if (err.response && err.response.data) {
                    errorMessage = err.response.data.error;
                } else if (typeof err.message === 'string') {
                    errorMessage = err.message;
                } else {
                    errorMessage = 'Failed to upload logo. Please try again.';
                }
            } catch (parseError) {
                errorMessage = 'Failed to upload logo. Please try again.';
            }

            setLogoError(errorMessage);
        } finally {
            setLoading(false);
            // Reset file input
            e.target.value = '';
        }
    }, [service, editingService, setServices]);

    // Handle logo delete
    const handleLogoDelete = useCallback(async () => {
        try {
            setLogoError(null);

            // Show confirmation dialog
            if (!window.confirm('Are you sure you want to delete this logo?')) {
                return;
            }

            setLoading(true);

            await deleteServiceLogo(service.id);

            // Update service data with null logo
            if (editingService) {
                setEditingService(prev => ({ ...prev, logo: null }));
            } else {
                setServiceData(prev => ({ ...prev, logo: null }));
            }

            // Update the services list in the parent component
            setServices(prevServices =>
                prevServices.map(s =>
                    s.id === service.id ? { ...s, logo: null } : s
                )
            );

            setLogoSuccess('Logo deleted successfully');
            setTimeout(() => setLogoSuccess(''), SUCCESS_MESSAGE_TIMEOUT);
        } catch (err) {
            console.error('Error deleting logo:', err);
            const errorMessage = err.message || 'Failed to delete logo. Please try again.';
            setLogoError(errorMessage.includes('AccessDenied') ? 'Server access error. Please try again later.' : errorMessage);
        } finally {
            setLoading(false);
        }
    }, [service, editingService, setServices]);

    if (loading && !serviceData) {
        return <div className={styles.text}>Loading settings...</div>;
    }

    return (
        <div className={styles.blockContainer}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept="image/png, image/jpeg"
            />

            <div className={styles.blockSection}>
                <div className={styles.secretsHeader}>
                    <h3 className={styles.secretsTitle}>Service Settings</h3>
                </div>

                {serviceSuccess && <div className={styles.success}>{serviceSuccess}</div>}
                {logoSuccess && <div className={styles.success}>{logoSuccess}</div>}
                {logoError && <div className={styles.error}>{logoError}</div>}

                {serviceData && !editingService && (
                    <ServiceSettingsView
                        service={serviceData}
                        onEdit={() => setEditingService({...serviceData})}
                    />
                )}

                {editingService && (
                    <ServiceSettingsEditor
                        serviceData={editingService}
                        loading={loading}
                        onChange={handleServiceChange}
                        onSubmit={handleServiceSubmit}
                        onCancel={handleServiceCancel}
                        onLogoUpload={handleLogoUpload}
                        onLogoDelete={handleLogoDelete}
                        error={serviceError}
                    />
                )}
            </div>

            <div className={styles.blockSection}>
                <div className={styles.secretsHeader}>
                    <h3 className={styles.secretsTitle}>Tools</h3>
                </div>

                {toolSuccess && <div className={styles.success}>{toolSuccess}</div>}

                {tools.length === 0 ? (
                    <p className={styles.emptyMessage}>No tools available for this service.</p>
                ) : (
                    <>
                        {!editingTool && (
                            <div className={styles.secretsList}>
                                {tools.map(tool => (
                                    <ToolListItem
                                        key={tool.id}
                                        tool={tool}
                                        onEdit={setEditingTool}
                                    />
                                ))}
                            </div>
                        )}

                        {editingTool && (
                            <ToolEditor
                                tool={editingTool}
                                loading={loading}
                                onChange={handleToolChange}
                                onJsonChange={handleJsonChange}
                                onSubmit={handleToolSubmit}
                                onCancel={handleToolCancel}
                                error={toolError}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default memo(ServiceSettings);
